import {
  AdminPanelSettings as AdminIcon,
  AssessmentTwoTone,
  ExitToApp,
  InboxTwoTone,
  Menu as MenuIcon,
  Person,
  SettingsOutlined,
  StorefrontTwoTone,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import cx from "classnames";
import { signOut } from "firebase/auth";
import { useSubscription } from "observable-hooks";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigation } from "react-navi";

import { OMGridLG } from "../components/max-width-grid";
import { useRolesContext } from "../contexts/roles";
import { auth } from "../firebase";
import { subjectHideHeader } from "../hooks/use-hide-header";
import { subjectHeader } from "../hooks/use-set-header";
import { subjectTitle } from "../hooks/use-set-title";
import { subjectShowProgress } from "../hooks/use-show-progress";
import { subjectSnackbar } from "../hooks/use-snackbar";
import Logo from "../images/logo.svg";
import styles from "./app.module.css";

const drawerWidth = 150;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    [theme.breakpoints.up("sm")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    [theme.breakpoints.up("sm")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
    backgroundColor: "white",
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  appBarMobile: {
    width: "100%",
    marginLeft: 0,
    backgroundColor: "white",
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  activeLink: {
    color: theme.palette.primary.main + "!important",
    fontWeight: "bold",
  },
  menuIcon: {
    fontSize: "40px",
    color: theme.palette.grey[500],
  },
  menuItem: {
    "&:hover p, &:hover svg": {
      color: theme.palette.grey[800],
    },
  },
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
  },
  listItemIcon: {
    minWidth: "35px",
  },
  content: {
    flexGrow: 1,
    backgroundColor: "#f2f4f9",
    padding: theme.spacing(3),
  },
  title: {
    maxWidth: "150px",
  },
  progressInMain: {
    position: "absolute",
    width: "100%",
  },
}));

export default function AppLayout({ children, window }) {
  const classes = useStyles();
  const theme = useTheme();
  const minWidth = useMediaQuery("(max-width:450px)");
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isPWAStandalone = useMediaQuery("(display-mode: standalone)");
  // Combined check: use mobile drawer if small screen OR running as installed PWA
  const useMobileDrawer = isSmallScreen || isPWAStandalone;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [title, setTitle] = useState();
  const { t } = useTranslation("Layout");
  const { isAdmin, canAccess } = useRolesContext();
  useSubscription(subjectTitle, setTitle);

  const [tunableHeader, setTunableHeader] = useState();
  useSubscription(subjectHeader, setTunableHeader);

  const [hideHeader, setHideHeader] = useState();
  useSubscription(subjectHideHeader, setHideHeader);

  const [showProgress, setShowProgress] = useState();
  useSubscription(subjectShowProgress, setShowProgress);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    subjectSnackbar.next({
      message: t("Successfully logout"),
    });
    signOut(auth);
  };

  const navigation = useNavigation();
  const currentNavigation = navigation.getCurrentValue();

  const drawer = (
    <div>
      <div className={classes.toolbar}>
        <span className={styles["logo-container"]}>
          <Logo className={styles["logo"]} />
        </span>
      </div>
      <Divider />
      <List>
        {[
          // Projects - visible to admin and projectOwner
          canAccess("projects") && [
            t("Projects"),
            "/projects",
            InboxTwoTone,
            ["/project", "/terms-of-service", "/create-project"],
          ],
          // Portfolio/Investments - visible to all authenticated users
          [t("Investments"), "/investments", AssessmentTwoTone],
          // Marketplace - visible to all authenticated users
          [t("Marketplace"), "/marketplace", StorefrontTwoTone],
          // Investor Profile - visible to all
          [t("Investor Profile"), "/investor-profile", Person],
          // Administration - visible only to admins
          isAdmin && [t("Administration"), "/admin", AdminIcon],
        ]
          .filter(Boolean)
          .map(([text, path, Icon, activePaths = []]) => {
            const isActive = [path, ...activePaths].some((activePath) =>
              currentNavigation.url.pathname.includes(activePath)
            );
            return (
              <ListItem
                alignItems="center"
                button
                classes={{ root: classes.menuItem }}
                key={text}
                onClick={() => {
                  handleDrawerToggle();
                  navigation.navigate(path);
                }}
                selected={isActive}
              >
                <Box direction="column" margin="0 auto" textAlign="center">
                  <Icon
                    className={cx(classes.menuIcon, {
                      [classes.activeLink]: isActive,
                    })}
                  />
                  <ListItemText
                    classes={{
                      secondary: cx({
                        [classes.activeLink]: isActive,
                      }),
                    }}
                    secondary={text}
                  />
                </Box>
              </ListItem>
            );
          })}
      </List>
      <Box mb={1}>
        <Divider />
      </Box>
      <List>
        <ListItem
          button
          classes={{
            root: cx({
              [classes.menuItem]:
                !currentNavigation.url.pathname.includes("/settings"),
            }),
          }}
          onClick={() => {
            handleDrawerToggle();
            navigation.navigate("/settings");
          }}
          selected={currentNavigation.url.pathname.includes("/settings")}
        >
          <ListItemIcon
            classes={{
              root: cx(classes.listItemIcon, {
                [classes.activeLink]:
                  currentNavigation.url.pathname.includes("/settings"),
              }),
            }}
          >
            <SettingsOutlined />
          </ListItemIcon>
          <ListItemText
            classes={{
              secondary: cx({
                [classes.activeLink]:
                  currentNavigation.url.pathname.includes("/settings"),
              }),
            }}
            secondary={t("Settings")}
          />
        </ListItem>

        <ListItem
          button
          classes={{ root: classes.menuItem }}
          onClick={handleLogout}
          style={{
            position: "fixed",
            bottom: 0,
            width: drawerWidth,
            textAlign: "center",
            marginBottom: 50,
          }}
        >
          <ListItemIcon classes={{ root: classes.listItemIcon }}>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText secondary={t("Log out")} />
        </ListItem>
      </List>
      <Box
        style={{
          position: "fixed",
          bottom: 0,
          width: drawerWidth,
          textAlign: "center",
          paddingBottom: 10,
        }}
      >
        <Typography variant="caption">
          <Link color="inherit" href="https://www.topmonks.com" target="_blank">
            TOPMONKS
          </Link>
        </Typography>
      </Box>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <div className={classes.root}>
      {!hideHeader && (
        <AppBar
          className={useMobileDrawer ? classes.appBarMobile : classes.appBar}
          elevation={0}
          position="fixed"
        >
          <Toolbar>
            <OMGridLG
              alignItems="center"
              container
              justifyContent="space-between"
              spacing={1}
            >
              {useMobileDrawer && (
                <Grid item xs={1}>
                  <IconButton
                    aria-label="open drawer"
                    className={classes.menuButton}
                    color="inherit"
                    edge="start"
                    onClick={handleDrawerToggle}
                  >
                    <MenuIcon />
                  </IconButton>
                </Grid>
              )}
              <Grid item>
                <Typography
                  classes={minWidth ? { root: classes.title } : {}}
                  component="h2"
                  noWrap
                  variant="h6"
                >
                  {title}
                </Typography>
              </Grid>
              <Grid item>{tunableHeader}</Grid>
            </OMGridLG>
          </Toolbar>
          {showProgress && <LinearProgress />}
        </AppBar>
      )}
      <nav
        aria-label="mailbox folders"
        className={useMobileDrawer ? undefined : classes.drawer}
      >
        {useMobileDrawer ? (
          <SwipeableDrawer
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            anchor={theme.direction === "rtl" ? "right" : "left"}
            classes={{
              paper: classes.drawerPaper,
            }}
            container={container}
            disableSwipeToOpen
            onClose={handleDrawerToggle}
            onOpen={handleDrawerToggle}
            open={mobileOpen}
            variant="temporary"
          >
            {drawer}
          </SwipeableDrawer>
        ) : (
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            open
            variant="permanent"
          >
            {drawer}
          </Drawer>
        )}
      </nav>
      {hideHeader && showProgress && (
        <LinearProgress classes={{ root: classes.progressInMain }} />
      )}
      <main className={classes.content}>
        {!hideHeader && <div className={classes.toolbar} />}
        {children}
      </main>
    </div>
  );
}

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
  window: PropTypes.instanceOf(window.constructor),
};
